import React from "react";

import BookmarkButton from "./BookmarkButton";
import ShareButton from "./ShareButton";
import {
  AvailabilityBanner,
  QuantityStepper,
  StarRating,
  SupplierAvatar,
} from "./DetailBits";
import { formatDistanceKm, formatMoney } from "../detailMappers";

const MAX_QUANTITY = 10;

const ProductPurchasePanel = ({
  product,
  supplier,
  supplierName,
  supplierLogo,
  notice,
  quantity,
  inCart,
  busy,
  busyAction,
  canSave,
  saved,
  savePending,
  onQuantityChange,
  onToggleSave,
  onAdd,
  onBuy,
  onViewCart,
  onVisitStore,
}) => {
  const { name, price, originalPrice, currency, pricePerKg, inStock } = product;

  const hasDiscount = Number.isFinite(originalPrice) && originalPrice > price;
  const percentOff = hasDiscount
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;

  const chips = [
    product.containerOption?.defaultTitle,
    product.volume ? `${product.volume} Kg` : null,
    product.brand?.defaultTitle,
  ].filter(Boolean);

  const distanceKm = supplier?.distanceKm;
  const distance =
    Number.isFinite(distanceKm) && distanceKm > 0
      ? `${formatDistanceKm(distanceKm)} away`
      : null;

  const blocked = Boolean(notice);

  return (
    <section className="sy-buy">
      <div className="sy-buy-head">
        <h2 className="sy-buy-title">{name}</h2>

        <div className="sy-buy-tools">
          {canSave && (
            <BookmarkButton
              saved={saved}
              pending={savePending}
              name={supplierName}
              onToggle={onToggleSave}
            />
          )}

          <ShareButton title={name} />
        </div>
      </div>

      {chips.length > 0 && (
        <ul className="sy-chip-row">
          {chips.map((chip) => (
            <li className="sy-chip" key={chip}>
              {chip}
            </li>
          ))}
        </ul>
      )}

      <div className="sy-buy-price">
        <b>{formatMoney(price, currency)}</b>

        {hasDiscount && (
          <>
            <s>{formatMoney(originalPrice, currency)}</s>
            <em>{percentOff}% off</em>
          </>
        )}
      </div>

      <p className="sy-buy-meta">
        {Number.isFinite(pricePerKg) && (
          <span>{formatMoney(pricePerKg, currency)} per Kg</span>
        )}

        <span className={`sy-stock${inStock ? "" : " is-out"}`}>
          {inStock ? "In stock" : "Out of stock"}
        </span>
      </p>

      <AvailabilityBanner notice={notice} />

      <div className="sy-buy-actions">
        <QuantityStepper
          value={quantity}
          max={MAX_QUANTITY}
          disabled={!inStock || blocked || inCart}
          onChange={onQuantityChange}
        />

        {!inStock ? (
          <button type="button" className="sy-btn sy-btn-primary" disabled>
            Out of Stock
          </button>
        ) : inCart ? (
          <button type="button" className="sy-btn sy-btn-primary" onClick={onViewCart}>
            View Cart
          </button>
        ) : (
          <>
            <button
              type="button"
              className="sy-btn sy-btn-outline"
              disabled={blocked || busy}
              onClick={onAdd}
            >
              {busy && busyAction === "add" ? "Adding…" : "Add to Cart"}
            </button>

            <button
              type="button"
              className="sy-btn sy-btn-primary"
              disabled={blocked || busy}
              onClick={onBuy}
            >
              {busy && busyAction === "buy" ? "Working…" : "Buy Now"}
            </button>
          </>
        )}
      </div>

      <div className="sy-seller">
        <SupplierAvatar name={supplierName} logoUrl={supplierLogo} size={44} />

        <div className="sy-seller-body">
          <span className="sy-seller-label">Sold by</span>
          <span className="sy-seller-name">{supplierName}</span>

          <span className="sy-seller-meta">
            {supplier?.totalRatings ? (
              <StarRating rating={supplier.rating} total={supplier.totalRatings} />
            ) : null}

            {distance && <i>{distance}</i>}
          </span>
        </div>

        {onVisitStore && (
          <button type="button" className="sy-seller-link" onClick={onVisitStore}>
            Visit store
          </button>
        )}
      </div>
    </section>
  );
};

export default ProductPurchasePanel;
